import React from "react";
import { RefreshCw, HelpCircle, Check, X, Mic, ChevronDown } from "lucide-react";
import { useTheme } from "../../../hooks/useTheme.js";
import { TIMING } from "../../../constants/interviewInstructions";
import StepHeader from "./StepHeader";

// Redesigned microphone check. All state and handlers come from
// useMicrophoneTest via InterviewInstructions — this file is presentation only.
// The old gradient level bar is replaced by the dots inside the blue disc:
// `liveLevel` (0-100) drives their bounce amplitude and the halo's scale.
const MicCheckStep = ({
  showMicHelp,
  setShowMicHelp,
  setErrorMessage,
  errorMessage,
  setMicStatus,
  micStatus,
  testMicrophone,
  permissionsStatus,
  requestMicPermission,
  isRequestingMic,
  selectedMic,
  setSelectedMic,
  availableMics,
  isTestingMic,
  liveLevel,
  lastVerdict,
  testedDeviceLabel,
  testPhase,
  handleNextStep,
  onBack,
  stepNumber,
  totalSteps,
  speakPromptText = "say a few words…",
  // Both halves of the combined step share one header; only the card inside
  // and the continue label differ.
  title = "Microphone & Camera",
  description = "Make sure your face is well lit and clearly visible, and that it's quiet enough for your mic to pick up your voice.",
  continueLabel = "Next - Check Camera",
  // Off for callers that already render their own heading (the mid-interview
  // mic recovery screen), and Back is hidden when there is nowhere to go back to.
  showHeader = true,
}) => {
  const { theme } = useTheme();
  const isLight = theme === "light";

  // SIGNAL_DEAD / DEVICE_ERROR are unusable-audio verdicts: hard-block with a
  // fix path. NO_SPEECH stays a gentle retry — never a wall.
  const isBlockingVerdict =
    micStatus === "failed" &&
    (lastVerdict === "SIGNAL_DEAD" || lastVerdict === "DEVICE_ERROR");

  const isGranted = permissionsStatus.microphone === "granted";
  const isVerified = micStatus === "verified";

  // Only react to level while we're actually listening for speech
  const level =
    isTestingMic && testPhase === "speaking"
      ? Math.min(100, Math.max(0, liveLevel || 0))
      : 0;
  const dotAmplitude = (level / 100) * 7;
  const haloScale = 1 + (level / 100) * 0.14;

  const cardClass = isLight
    ? "bg-white border-[#ECEDF5] shadow-[0_1px_2px_rgba(16,24,40,0.04)]"
    : "bg-gray-800/50 border-gray-700/50 backdrop-blur-sm";

  const statusText = () => {
    if (isVerified) return "Your microphone sounds good";
    if (isTestingMic && testPhase === "speaking") return speakPromptText;
    if (isTestingMic) return "Checking your microphone…";
    if (micStatus === "failed") return "Let's try again — tap to retry";
    return "Tap the circle when you're ready";
  };

  const micPicker = (availableMics || []).length > 0 && (
    <div className="relative inline-flex items-center">
      <Mic
        className={`w-3.5 h-3.5 absolute left-3.5 pointer-events-none ${
          isLight ? "text-slate-500" : "text-gray-300"
        }`}
        strokeWidth={1.75}
      />
      <select
        data-testid="mic-picker"
        value={selectedMic || ""}
        onChange={(e) => setSelectedMic(e.target.value)}
        disabled={isTestingMic}
        // max-w bounds the CLOSED box; a native option list sizes itself to its
        // longest label regardless, so the option text is truncated too.
        className={`appearance-none pl-9 pr-9 py-2 rounded-full text-[12px] max-w-[260px] truncate cursor-pointer ${
          isLight
            ? "bg-[#F1F2F5] text-slate-600"
            : "bg-gray-700 text-gray-100"
        } ${isTestingMic ? "opacity-50 cursor-not-allowed" : ""}`}
      >
        {(availableMics || []).map((mic, i) => {
          const label = mic.label || `Microphone ${i + 1}`;
          return (
            <option key={mic.deviceId} value={mic.deviceId} title={label}>
              {label.length > 45 ? `${label.slice(0, 45)}…` : label}
            </option>
          );
        })}
      </select>
      <ChevronDown
        className={`w-4 h-4 absolute right-3 pointer-events-none ${
          isLight ? "text-slate-400" : "text-gray-400"
        }`}
        strokeWidth={1.75}
      />
    </div>
  );

  // SUBSECTION: Help panel — same copy and handlers as before
  if (showMicHelp) {
    const helpSteps = [
      {
        title: "Enable microphone access",
        body: "Enable microphone in browser Settings → Privacy & Security → Site Permissions.",
      },
      {
        title: "Select correct device",
        body: "Choose the correct microphone from the dropdown menu on the previous screen.",
      },
      {
        title: "Test with Voice Recorder",
        body: "Use your device’s Voice Recorder app to verify microphone functionality.",
      },
      {
        title: "Try alternatives",
        body: "Restart browser, try incognito mode, or use a different browser/device.",
      },
    ];

    return (
      <div className="w-full">
        <StepHeader
          stepNumber={stepNumber}
          totalSteps={totalSteps}
          title="Steps to fix microphone issues"
          description="Work through these in order — most problems clear up after the first two."
        />

        <div className={`mt-7 rounded-2xl border ${cardClass}`}>
          <ul
            className={`px-6 divide-y ${
              isLight ? "divide-[#F1F2F7]" : "divide-gray-700/50"
            }`}
          >
            {helpSteps.map((step, index) => (
              <li key={step.title} className="flex items-start gap-3.5 py-4">
                <span
                  className={`w-6 h-6 shrink-0 rounded-full flex items-center justify-center text-[11px] font-medium ${
                    isLight
                      ? "bg-[#F1F2F7] text-slate-500"
                      : "bg-gray-700 text-gray-300"
                  }`}
                >
                  {index + 1}
                </span>
                <span className="min-w-0">
                  <span
                    className={`block text-[14px] font-semibold leading-tight ${
                      isLight ? "text-slate-900" : "text-white"
                    }`}
                  >
                    {step.title}
                  </span>
                  <span
                    className={`block text-[12px] leading-relaxed mt-1 ${
                      isLight ? "text-slate-400" : "text-gray-400"
                    }`}
                  >
                    {step.body}
                  </span>
                </span>
              </li>
            ))}

            <li className="flex items-start gap-3.5 py-4">
              <span className="w-6 h-6 shrink-0 rounded-full flex items-center justify-center text-[11px] font-semibold bg-orange-100 text-orange-500">
                !
              </span>
              <span className="min-w-0">
                <span
                  className={`block text-[14px] font-semibold leading-tight ${
                    isLight ? "text-slate-900" : "text-white"
                  }`}
                >
                  Need support?
                </span>
                <span
                  className={`block text-[12px] leading-relaxed mt-1 ${
                    isLight ? "text-slate-400" : "text-gray-400"
                  }`}
                >
                  If you're still facing issues, contact{" "}
                  <a
                    href="mailto:support@geniehire.ai"
                    className="text-blue-500 font-medium hover:underline"
                  >
                    support@geniehire.ai
                  </a>{" "}
                  or call{" "}
                  <a
                    href="tel:+918766635744"
                    className="text-blue-500 font-medium hover:underline"
                  >
                    +91 87666 35744
                  </a>
                </span>
              </span>
            </li>
          </ul>
        </div>

        <div className="flex justify-end mt-6">
          <button
            onClick={() => {
              setShowMicHelp(false);
              setErrorMessage(null);
              setMicStatus("idle");
              setTimeout(
                () => testMicrophone("help_return"),
                TIMING.HELP_RETEST_DELAY_MS
              );
            }}
            className="px-7 py-2.5 rounded-full text-[14px] font-medium text-white bg-gradient-to-r from-[#2F64FF] to-[#FB54AE] hover:opacity-90 transition-opacity"
          >
            Back to mic test
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <style>{`
        @keyframes ghMicDot {
          0%, 100% { transform: translateY(calc(var(--gh-amp, 0px) * 0.45)); }
          50%      { transform: translateY(calc(var(--gh-amp, 0px) * -1)); }
        }
        .gh-mic-dot { animation: ghMicDot 0.6s ease-in-out infinite; }
      `}</style>

      {showHeader && (
        <StepHeader
          stepNumber={stepNumber}
          totalSteps={totalSteps}
          title={title}
          description={description}
        />
      )}

      <div className={`mt-7 rounded-2xl border ${cardClass}`}>
        {/* Card header — title + completion indicator */}
        <div
          className={`flex items-center justify-between px-6 py-4 border-b ${
            isLight ? "border-[#F1F2F7]" : "border-gray-700/50"
          }`}
        >
          <h2
            className={`text-[13px] font-medium ${
              isLight ? "text-slate-700" : "text-gray-200"
            }`}
          >
            Test Microphone
          </h2>

          <span
            className={`w-[18px] h-[18px] rounded-full flex items-center justify-center ${
              isVerified
                ? "bg-[#22C55E]"
                : micStatus === "failed"
                ? "bg-red-500"
                : isLight
                ? "border border-[#D8DAE2]"
                : "border border-gray-600"
            }`}
          >
            {isVerified && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
            {micStatus === "failed" && (
              <X className="w-3 h-3 text-white" strokeWidth={3} />
            )}
          </span>
        </div>

        <div className="px-6 py-8">
          {permissionsStatus.microphone === "denied" ? (
            <p className="text-red-400 text-[13px] text-center py-10">
              Microphone access is denied. Please enable it in your browser
              settings and refresh the page to continue.
            </p>
          ) : !isGranted ? (
            <div className="flex flex-col items-center gap-4 py-10">
              <p
                className={`text-[13px] text-center ${
                  isLight ? "text-slate-500" : "text-gray-400"
                }`}
              >
                We need access to your microphone to run this check.
              </p>
              <button
                onClick={requestMicPermission}
                disabled={isRequestingMic}
                className={`px-7 py-2.5 rounded-full text-[14px] font-medium text-white transition-opacity ${
                  isRequestingMic
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-gradient-to-r from-[#2F64FF] to-[#FB54AE] hover:opacity-90"
                }`}
              >
                {isRequestingMic ? (
                  <span className="flex items-center justify-center">
                    <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                    Requesting…
                  </span>
                ) : (
                  "Grant microphone permission"
                )}
              </button>
            </div>
          ) : isBlockingVerdict ? (
            /* SIGNAL_DEAD / DEVICE_ERROR: the device is unusable — name it and
               offer the fix path. Never says "speak louder". */
            <div className="flex flex-col items-center gap-3 py-4">
              <h3
                className={`text-[20px] font-medium text-center ${
                  isLight ? "text-slate-900" : "text-white"
                }`}
              >
                We can't hear anything from your microphone
              </h3>
              <p
                className={`text-[13px] text-center ${
                  isLight ? "text-slate-500" : "text-gray-300"
                }`}
              >
                {lastVerdict === "SIGNAL_DEAD"
                  ? `"${testedDeviceLabel || "Your microphone"}" is connected but is not sending any audio.`
                  : errorMessage}
              </p>
              {/* <ul
                className={`text-[12px] text-left list-disc pl-5 space-y-1 ${
                  isLight ? "text-slate-400" : "text-gray-400"
                }`}
              >
                <li>Check the microphone's physical mute switch or key</li>
                <li>Check your system's input volume and mute settings</li>
                <li>Or pick a different microphone below and retry</li>
              </ul> */}

              <div className="mt-3">{micPicker}</div>

              <div className="flex flex-wrap items-center justify-center gap-3 mt-3">
                <button
                  onClick={() => testMicrophone("retry_after_dead")}
                  disabled={!selectedMic || isTestingMic}
                  className={`px-6 py-2 rounded-full text-[13px] font-medium transition-opacity ${
                    !selectedMic || isTestingMic
                      ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                      : "bg-gradient-to-r from-[#2F64FF] to-[#FB54AE] text-white hover:opacity-90"
                  }`}
                >
                  Retry with selected microphone
                </button>
                <button
                  onClick={() => setShowMicHelp(true)}
                  className={`px-5 py-2 rounded-full text-[13px] font-medium border flex items-center gap-2 transition-colors ${
                    isLight
                      ? "border-[#DDD8F0] text-slate-600 hover:bg-[#F7F7FC]"
                      : "border-gray-600 text-gray-200 hover:bg-gray-700/40"
                  }`}
                >
                  <HelpCircle size={14} />
                  Need help?
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center">
              {/* Halo + disc. Tap to run the check. */}
              <button
                onClick={() => testMicrophone("button")}
                disabled={isTestingMic || !selectedMic || isVerified}
                aria-label="Start microphone check"
                className={`rounded-full flex items-center justify-center transition-transform duration-100 ${
                  isLight ? "bg-[#F1F2F5]" : "bg-gray-700/40"
                } ${
                  isTestingMic || !selectedMic || isVerified
                    ? "cursor-default"
                    : "cursor-pointer"
                }`}
                style={{
                  width: 152,
                  height: 152,
                  transform: `scale(${haloScale})`,
                }}
              >
                <span
                  className="rounded-full flex items-center justify-center transition-colors"
                  style={{
                    width: 112,
                    height: 112,
                    // Brand gradient while testing; solid green once verified,
                    // so "passed" still reads as a distinct state.
                    background: isVerified
                      ? "#22C55E"
                      : "linear-gradient(135deg, #2F64FF 0%, #FB54AE 100%)",
                    boxShadow: level
                      ? `0 0 0 ${4 + (level / 100) * 10}px rgba(122,89,214,${
                          0.06 + (level / 100) * 0.1
                        })`
                      : "none",
                  }}
                >
                  {isVerified ? (
                    <Check className="w-10 h-10 text-white" strokeWidth={2.5} />
                  ) : (
                    <span
                      className="flex items-center gap-[7px]"
                      style={{ "--gh-amp": `${dotAmplitude}px` }}
                    >
                      {[0, 1, 2].map((dot) => (
                        <span
                          key={dot}
                          className={`block w-[9px] h-[9px] rounded-full bg-white ${
                            level > 0 ? "gh-mic-dot" : ""
                          }`}
                          style={{ animationDelay: `${dot * 0.12}s` }}
                        />
                      ))}
                    </span>
                  )}
                </span>
              </button>

              {/* Status line */}
              <p
                className={`flex items-center gap-1.5 text-[14px] mt-6 ${
                  isLight ? "text-slate-500" : "text-gray-400"
                }`}
              >
                {isTestingMic && (
                  <span className="w-1.5 h-1.5 rounded-full bg-gradient-to-br from-[#2F64FF] to-[#FB54AE] animate-pulse" />
                )}
                {statusText()}
              </p>

              <div className="mt-4">{micPicker}</div>

              {errorMessage && !isVerified && (
                <p className="text-red-400 text-[12px] mt-4 text-center whitespace-pre-line">
                  {errorMessage}
                </p>
              )}

              {micStatus === "failed" && (
                <button
                  onClick={() => setShowMicHelp(true)}
                  className={`mt-4 px-5 py-2 rounded-full text-[13px] font-medium border flex items-center gap-2 transition-colors ${
                    isLight
                      ? "border-[#DDD8F0] text-slate-600 hover:bg-[#F7F7FC]"
                      : "border-gray-600 text-gray-200 hover:bg-gray-700/40"
                  }`}
                >
                  <HelpCircle size={14} />
                  Need help?
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between mt-6">
        {onBack ? (
          <button
            onClick={onBack}
            disabled={isTestingMic}
            className={`px-7 py-2.5 rounded-full text-[14px] font-medium border transition-colors ${
              isTestingMic ? "opacity-50 cursor-not-allowed" : ""
            } ${
              isLight
                ? "bg-white border-[#DDD8F0] text-slate-600 hover:bg-[#F7F7FC]"
                : "bg-transparent border-gray-600 text-gray-200 hover:bg-gray-700/40"
            }`}
          >
            Back
          </button>
        ) : (
          <span />
        )}

        <button
          onClick={handleNextStep}
          disabled={!isVerified}
          className={`px-7 py-2.5 rounded-full text-[14px] font-medium transition-opacity ${
            isVerified
              ? "bg-gradient-to-r from-[#2F64FF] to-[#FB54AE] text-white hover:opacity-90"
              : "bg-gray-200 text-gray-400 cursor-not-allowed dark:bg-gray-600 dark:text-gray-400"
          }`}
        >
          {continueLabel}
        </button>
      </div>
    </div>
  );
};

export default MicCheckStep;
